import * as React from 'react';
import { Flex } from '../styles/layout';
import { useLocation, useHistory } from 'react-router-dom';
import { ReactNode } from 'react';
import styled, { DefaultTheme } from 'styled-components/macro';

export type TabGroupItem = {
  value: string;
  title: ReactNode;
  disabled?: boolean;
  isHidden?: boolean;
  component?: ReactNode;
};

type TabGroupProps = {
  items: TabGroupItem[];
  onChange?: (item: TabGroupItem) => void;
};

const getColor = (props: DefaultTheme) => {
  switch (props.themeName) {
    case 'cdk':
      return props.colors.black[100];
    default:
      return props.colors.primary[100];
  }
};

const TabItem = styled.button<{
  isActive: boolean;
  disabled?: boolean;
}>`
  all: unset;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.2 : 1)};

  display: inline-flex;
  align-items: center;
  vertical-align: top;
  white-space: nowrap;
  border-bottom: ${({ isActive, theme }) =>
    isActive ? `4px solid ${getColor(theme)} ` : '4px solid transparent'};
  color: ${({ isActive, theme }) =>
    isActive ? getColor(theme) : theme.colors.neutral[60]};
  padding: 0 16px 8px;

  transition: color 0.2s ease-in-out;
  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
`;

/**
 * ⚙️ Tabs + Forms Convention
 *
 * - If the form is shared across all tabs (e.g., editing one entity in multiple sections),
 *   render <TabGroup /> inside a single <Formik> or form wrapper — this ensures all fields
 *   share the same form state and submit together.
 *   👉 Example: EditUserProfile.tsx
 *
 * - If each tab contains its own independent form, render the <TabGroup /> as a *child*
 *   of each tab’s form component. Each form then manages its own state and submission. Please use
 *    const { activeTab } = useTabFromQueryString() to parent components to determine the active tab
 *   👉 Example: ManageUserEntryPage.tsx
 */

export const TabGroup = ({ items, onChange }: TabGroupProps) => {
  const location = useLocation();
  const history = useHistory();

  // Memoize visible items so effects don't re-run unnecessarily
  const visibleItems = React.useMemo(
    () => items.filter(i => !i.isHidden),
    [items]
  );

  //  lookup helper for a value coming from the query string
  const findItemByQS = React.useCallback(
    (qs: string | null): TabGroupItem | undefined =>
      qs ? visibleItems.find(i => i.value === qs) : undefined,
    [visibleItems]
  );

  // Initial active tab from ?tab=... if valid; otherwise first visible
  const [activeTab, setActiveTab] = React.useState<string>(() => {
    const qs = new URLSearchParams(location.search).get('tab');
    const match = visibleItems.find(i => i.value === qs);
    return match?.value ?? visibleItems[0]?.value ?? '';
  });

  // Keep local state in sync with URL changes (back/forward/external edits)
  // Also write a fallback ?tab=... if URL has none/invalid while we have items.
  React.useEffect(() => {
    const qs = new URLSearchParams(location.search).get('tab');
    const match = findItemByQS(qs);

    if (match) {
      if (match.value !== activeTab) {
        setActiveTab(match.value);
        onChange?.(match);
      }
      return;
    }

    // No valid tab in URL: fall back to the first visible item and push it to the URL
    const fallback = visibleItems[0];
    if (!fallback) return; // nothing to show
    if (activeTab !== fallback.value) {
      setActiveTab(fallback.value);
      onChange?.(fallback);
    }
    const next = new URLSearchParams(location.search);
    next.set('tab', fallback.value);
    // Use push (not replace) to preserve browser history semantics
    history.push({
      pathname: location.pathname,
      search: `?${next.toString()}`,
    });
  }, [
    location.pathname,
    location.search,
    activeTab,
    findItemByQS,
    visibleItems,
    onChange,
    history,
  ]);

  // Update the URL to reflect a new active tab value
  const applyQS = React.useCallback(
    (value: string) => {
      const next = new URLSearchParams(location.search);
      // Short-circuit if the URL already has the same tab to avoid redundant pushes
      if (next.get('tab') === value) return;

      next.set('tab', value);
      history.push({
        pathname: location.pathname,
        search: `?${next.toString()}`,
      });
    },
    [history, location.pathname, location.search]
  );

  // Click handler: update state, notify, and sync the URL
  const handleClick = React.useCallback(
    (item: TabGroupItem) => {
      const { value, disabled } = item;
      if (disabled || value === activeTab) return;
      setActiveTab(value);
      onChange?.(item);
      applyQS(value);
    },
    [activeTab, onChange, applyQS]
  );

  if (!visibleItems.length) return null;

  return (
    <>
      <Flex
        role='tablist'
        aria-label='Tabs'
        flexDirection='row'
        gap='8px'
        style={{
          borderBottom: '1px solid rgb(247, 248, 250)',
          width: 'fit-content',
          marginBottom: '32px',
        }}
      >
        {visibleItems.map(item => {
          const selected = item.value === activeTab;
          return (
            <TabItem
              key={`${item.title}tab`}
              type='button'
              role='tab'
              aria-selected={selected}
              aria-disabled={item.disabled || undefined}
              onClick={() => handleClick(item)}
              isActive={selected}
              disabled={item.disabled}
            >
              {item.title}
            </TabItem>
          );
        })}
      </Flex>

      {visibleItems.map(item => {
        if (item.value !== activeTab) return null;
        if (!item.component) return null;
        return (
          <div
            key={`${item.title}tabpanel`}
            role='tabpanel'
            aria-labelledby={item.value}
            style={{ marginTop: '16px' }}
          >
            {item.component}
          </div>
        );
      })}
    </>
  );
};
